import { z } from "zod";
import { bookGenerationCostEvidenceSourceSchema, bookGenerationPromptBindingV1Schema, bookGenerationProviderCallV1Schema, bookGenerationProviderSchema, bookGenerationSourceBindingV1Schema, } from "./book-generation.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const gitShaSchema = z.string().regex(/^[a-f0-9]{40}$/u);
const timestampSchema = z.string().datetime({ offset: true });
const opaqueIdSchema = z.string().trim().min(1).max(240);
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[A-Z0-9][A-Z0-9_]*$/u);
function requireUnique(values, context, path, label) {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", path, message: `${label} must be unique.` });
}
function requirePdfPageCount(value, context) {
    if (value.contentType === "application/pdf" && value.pageCount === undefined)
        context.addIssue({
            code: "custom",
            path: ["pageCount"],
            message: "PDF evidence requires an exact page count.",
        });
}
export const bookProductionEvidenceContractSchema = z.literal("BOOK_PRODUCTION_EVIDENCE_V1");
export const bookProductionProductSchema = z.enum([
    "BEDTIME",
    "COMIC",
    "RECIPE",
    "MY_STORY",
    "OUR_STORY",
]);
export const bookProductionFamilySchema = z.enum([
    "PICTURE_BOOK",
    "COMIC_CORE",
    "RECIPE",
    "NARRATIVE",
]);
export const bookProductionVersionBindingV1Schema = z
    .object({
    id: z.string().trim().min(1).max(160),
    version: z.string().trim().min(1).max(100),
    hashSha256: sha256Schema,
})
    .strict();
export const bookProductionArtifactBindingV1Schema = z
    .object({
    artifactId: opaqueIdSchema,
    role: safeCodeSchema,
    objectVersionId: z.string().trim().min(1).max(500),
    checksumSha256: sha256Schema,
    byteSize: z.number().int().positive().max(2_000_000_000),
    contentType: z.string().trim().min(1).max(200),
    pageCount: z.number().int().positive().max(2_000).optional(),
})
    .strict()
    .superRefine(requirePdfPageCount);
export const bookProductionArtifactV1Schema = z
    .object({
    artifactId: opaqueIdSchema,
    role: safeCodeSchema,
    objectVersionId: z.string().trim().min(1).max(500),
    checksumSha256: sha256Schema,
    byteSize: z.number().int().positive().max(2_000_000_000),
    contentType: z.string().trim().min(1).max(200),
    pageCount: z.number().int().positive().max(2_000).optional(),
    immutable: z.literal(true),
    createdAt: timestampSchema,
})
    .strict()
    .superRefine(requirePdfPageCount);
export const bookProductionAssetReferenceV1Schema = z
    .object({
    assetId: opaqueIdSchema,
    objectVersionId: z.string().trim().min(1).max(500),
    checksumSha256: sha256Schema,
})
    .strict();
export const bookProductionPromptEvidenceV1Schema = bookGenerationPromptBindingV1Schema.extend({
    bindingId: opaqueIdSchema,
});
export const bookProductionProviderCallEvidenceV1Schema = bookGenerationProviderCallV1Schema.extend({
    callId: opaqueIdSchema,
});
export const bookProductionBudgetReservationEvidenceV1Schema = z
    .object({
    reservationId: opaqueIdSchema,
    executionId: opaqueIdSchema,
    providerCallId: opaqueIdSchema.optional(),
    provider: bookGenerationProviderSchema,
    estimatedCostMicros: z.number().int().nonnegative(),
    maximumCostMicros: z.number().int().positive(),
    actualCostMicros: z.number().int().nonnegative().optional(),
    state: z.enum(["RESERVED", "RECONCILED", "RELEASED"]),
    costEvidenceSource: bookGenerationCostEvidenceSourceSchema,
    createdAt: timestampSchema,
    settledAt: timestampSchema.optional(),
})
    .strict()
    .superRefine((reservation, context) => {
    const reconciled = reservation.state === "RECONCILED";
    const settled = reservation.state !== "RESERVED";
    if (settled !== Boolean(reservation.settledAt))
        context.addIssue({
            code: "custom",
            path: ["settledAt"],
            message: "Settled reservations require settledAt; active reservations must omit it.",
        });
    if (reconciled !== Boolean(reservation.providerCallId))
        context.addIssue({
            code: "custom",
            path: ["providerCallId"],
            message: "Reconciled reservations require one provider call; other states must omit it.",
        });
    if (reconciled !== (reservation.actualCostMicros !== undefined))
        context.addIssue({
            code: "custom",
            path: ["actualCostMicros"],
            message: "Only reconciled reservations record actual cost.",
        });
    if (reservation.actualCostMicros !== undefined &&
        reservation.actualCostMicros > reservation.maximumCostMicros)
        context.addIssue({
            code: "custom",
            path: ["actualCostMicros"],
            message: "Actual cost cannot exceed the reservation maximum.",
        });
    if (reservation.costEvidenceSource === "NO_PROVIDER_COST" &&
        (reservation.actualCostMicros ?? 0) !== 0)
        context.addIssue({
            code: "custom",
            path: ["actualCostMicros"],
            message: "NO_PROVIDER_COST requires zero actual cost.",
        });
});
export const bookProductionBudgetEvidenceV1Schema = z
    .object({
    budgetId: opaqueIdSchema,
    policyBinding: bookProductionVersionBindingV1Schema,
    dollarLimitMicros: z.number().int().positive(),
    actualCostMicros: z.number().int().nonnegative(),
    reservations: z.array(bookProductionBudgetReservationEvidenceV1Schema).max(500),
})
    .strict();
export const bookProductionExecutionEvidenceClassSchema = z.enum([
    "DETERMINISTIC",
    "SIMULATED",
    "RECORDED_ACTUAL",
]);
export const bookProductionStageExecutionV1Schema = z
    .object({
    executionId: opaqueIdSchema,
    stageId: safeCodeSchema,
    jobId: opaqueIdSchema,
    rootJobId: opaqueIdSchema,
    retryOfJobId: opaqueIdSchema.optional(),
    attempt: z.number().int().nonnegative().max(100),
    executor: z.enum(["DETERMINISTIC", "EXTERNAL_MODEL", "HUMAN"]),
    executionEvidence: bookProductionExecutionEvidenceClassSchema,
    state: z.enum(["SUCCEEDED", "FAILED", "BLOCKED"]),
    sourceBinding: bookGenerationSourceBindingV1Schema,
    promptBindingId: opaqueIdSchema.optional(),
    providerCallIds: z.array(opaqueIdSchema).max(100),
    budgetReservationIds: z.array(opaqueIdSchema).max(100),
    prerequisiteArtifactIds: z.array(opaqueIdSchema).max(500),
    outputArtifactIds: z.array(opaqueIdSchema).max(500),
    recordedAt: timestampSchema,
})
    .strict()
    .superRefine((execution, context) => {
    requireUnique(execution.providerCallIds, context, ["providerCallIds"], "Provider-call IDs");
    requireUnique(execution.budgetReservationIds, context, ["budgetReservationIds"], "Budget reservation IDs");
    requireUnique(execution.prerequisiteArtifactIds, context, ["prerequisiteArtifactIds"], "Prerequisite artifact IDs");
    requireUnique(execution.outputArtifactIds, context, ["outputArtifactIds"], "Output artifact IDs");
    if (execution.executor === "DETERMINISTIC" && execution.executionEvidence !== "DETERMINISTIC")
        context.addIssue({
            code: "custom",
            path: ["executionEvidence"],
            message: "Deterministic stages require deterministic execution evidence.",
        });
    if (execution.executor !== "DETERMINISTIC" && execution.executionEvidence === "DETERMINISTIC")
        context.addIssue({
            code: "custom",
            path: ["executionEvidence"],
            message: "External and human stages cannot claim deterministic execution evidence.",
        });
    if (execution.state === "SUCCEEDED" && execution.outputArtifactIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["outputArtifactIds"],
            message: "Successful stages require at least one immutable output artifact.",
        });
    if (execution.attempt === 0 && execution.retryOfJobId)
        context.addIssue({
            code: "custom",
            path: ["retryOfJobId"],
            message: "An initial attempt cannot identify a predecessor.",
        });
    if (execution.attempt > 0 && !execution.retryOfJobId)
        context.addIssue({
            code: "custom",
            path: ["retryOfJobId"],
            message: "Retry attempts require the exact predecessor job identifier.",
        });
});
function artifactBindingArraySchema(minimum = 1) {
    return z.array(bookProductionArtifactBindingV1Schema).min(minimum).max(500);
}
export const bookProductionReadinessDimensionSchema = z.enum([
    "DIGITAL_REVIEW",
    "ACCESSIBILITY",
    "PRINT",
    "PHYSICAL_PROOF",
    "FULFILLMENT",
    "DEPLOYMENT",
]);
export const bookProductionEvaluationV1Schema = z
    .object({
    evaluationId: opaqueIdSchema,
    readinessDimension: bookProductionReadinessDimensionSchema,
    evaluator: z.enum(["DETERMINISTIC", "MODEL", "HUMAN"]),
    rubricBinding: bookProductionVersionBindingV1Schema,
    sourceBinding: bookGenerationSourceBindingV1Schema,
    artifactBindings: artifactBindingArraySchema(),
    providerCallIds: z.array(opaqueIdSchema).max(100),
    outcome: z.enum(["PASSED", "FAILED", "REVIEW_REQUIRED"]),
    findingsArtifactId: opaqueIdSchema.optional(),
    evaluatedAt: timestampSchema,
})
    .strict()
    .superRefine((evaluation, context) => {
    requireUnique(evaluation.artifactBindings.map(({ artifactId }) => artifactId), context, ["artifactBindings"], "Evaluation artifact bindings");
    requireUnique(evaluation.providerCallIds, context, ["providerCallIds"], "Provider-call IDs");
    if (evaluation.evaluator === "MODEL" && evaluation.providerCallIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["providerCallIds"],
            message: "Model evaluations require recorded provider-call evidence.",
        });
    if (evaluation.evaluator !== "MODEL" && evaluation.providerCallIds.length > 0)
        context.addIssue({
            code: "custom",
            path: ["providerCallIds"],
            message: "Only model evaluations may bind provider calls.",
        });
});
export const bookProductionApprovalDecisionV1Schema = z
    .object({
    approverId: opaqueIdSchema,
    decision: z.enum(["PENDING", "APPROVED", "REJECTED", "REVISION_REQUESTED"]),
    decidedAt: timestampSchema.nullable(),
})
    .strict()
    .superRefine((decision, context) => {
    if ((decision.decision === "PENDING") !== (decision.decidedAt === null))
        context.addIssue({
            code: "custom",
            path: ["decidedAt"],
            message: "Pending decisions omit a time; terminal decisions require one.",
        });
});
export const bookProductionApprovalV1Schema = z
    .object({
    approvalId: opaqueIdSchema,
    readinessDimension: bookProductionReadinessDimensionSchema,
    approvalType: safeCodeSchema,
    revisionId: opaqueIdSchema,
    authority: z.enum(["SIMULATED", "RECORDED_ACTUAL"]),
    sourceBinding: bookGenerationSourceBindingV1Schema,
    policyBinding: bookProductionVersionBindingV1Schema,
    artifactBindings: artifactBindingArraySchema(),
    requiredApproverIds: z.array(opaqueIdSchema).min(1).max(20),
    decisions: z.array(bookProductionApprovalDecisionV1Schema).min(1).max(20),
    createdAt: timestampSchema,
})
    .strict()
    .superRefine((approval, context) => {
    requireUnique(approval.artifactBindings.map(({ artifactId }) => artifactId), context, ["artifactBindings"], "Approval artifact bindings");
    requireUnique(approval.requiredApproverIds, context, ["requiredApproverIds"], "Required approver IDs");
    requireUnique(approval.decisions.map(({ approverId }) => approverId), context, ["decisions"], "Approval decision IDs");
    const required = new Set(approval.requiredApproverIds);
    const decided = new Set(approval.decisions.map(({ approverId }) => approverId));
    if (required.size !== decided.size ||
        [...required].some((approverId) => !decided.has(approverId)))
        context.addIssue({
            code: "custom",
            path: ["decisions"],
            message: "Approval decisions must exactly cover the required approver set.",
        });
});
export const bookProductionAccessibilityEvidenceV1Schema = z
    .object({
    evidenceId: opaqueIdSchema,
    readinessDimension: bookProductionReadinessDimensionSchema,
    artifactBindings: artifactBindingArraySchema(),
    taggedPdf: z.boolean(),
    readingOrderVerified: z.boolean(),
    assistiveTechnologyTested: z.boolean(),
    checkedAt: timestampSchema,
})
    .strict()
    .superRefine((evidence, context) => requireUnique(evidence.artifactBindings.map(({ artifactId }) => artifactId), context, ["artifactBindings"], "Accessibility artifact bindings"));
export const bookProductionPhysicalProofEvidenceV1Schema = z
    .object({
    evidenceId: opaqueIdSchema,
    readinessDimension: bookProductionReadinessDimensionSchema,
    artifactBindings: artifactBindingArraySchema(),
    disposition: z.enum(["PENDING", "APPROVED", "REJECTED"]),
    reviewerId: opaqueIdSchema.optional(),
    reviewedAt: timestampSchema.optional(),
})
    .strict()
    .superRefine((proof, context) => {
    requireUnique(proof.artifactBindings.map(({ artifactId }) => artifactId), context, ["artifactBindings"], "Physical-proof artifact bindings");
    const reviewed = proof.disposition !== "PENDING";
    if (reviewed !== Boolean(proof.reviewerId) || reviewed !== Boolean(proof.reviewedAt))
        context.addIssue({
            code: "custom",
            path: ["reviewerId"],
            message: "Terminal physical-proof decisions require reviewer and review time evidence.",
        });
});
export const bookProductionDeploymentEvidenceV1Schema = z
    .object({
    evidenceId: opaqueIdSchema,
    readinessDimension: bookProductionReadinessDimensionSchema,
    artifactBindings: artifactBindingArraySchema(),
    releaseSha: gitShaSchema,
    environment: z.string().trim().min(1).max(100),
    observedAt: timestampSchema,
})
    .strict()
    .superRefine((evidence, context) => requireUnique(evidence.artifactBindings.map(({ artifactId }) => artifactId), context, ["artifactBindings"], "Deployment artifact bindings"));
export const bookProductionEvidenceReferenceV1Schema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("EVALUATION"), evidenceId: opaqueIdSchema }).strict(),
    z.object({ kind: z.literal("APPROVAL"), evidenceId: opaqueIdSchema }).strict(),
    z.object({ kind: z.literal("ACCESSIBILITY"), evidenceId: opaqueIdSchema }).strict(),
    z.object({ kind: z.literal("PHYSICAL_PROOF"), evidenceId: opaqueIdSchema }).strict(),
    z.object({ kind: z.literal("DEPLOYMENT"), evidenceId: opaqueIdSchema }).strict(),
]);
export const bookProductionReadinessGateV1Schema = z
    .object({
    gateId: opaqueIdSchema,
    dimension: bookProductionReadinessDimensionSchema,
    status: z.enum(["PASSED", "BLOCKED", "EXTERNAL_GATE"]),
    policyBinding: bookProductionVersionBindingV1Schema,
    sourceBinding: bookGenerationSourceBindingV1Schema,
    artifactBindings: z.array(bookProductionArtifactBindingV1Schema).max(500),
    evidenceReferences: z.array(bookProductionEvidenceReferenceV1Schema).max(500),
    blockerCode: safeCodeSchema.optional(),
    evaluatedAt: timestampSchema,
})
    .strict()
    .superRefine((gate, context) => {
    requireUnique(gate.artifactBindings.map(({ artifactId }) => artifactId), context, ["artifactBindings"], "Gate artifact bindings");
    requireUnique(gate.evidenceReferences.map(({ evidenceId }) => evidenceId), context, ["evidenceReferences"], "Gate evidence references");
    if (gate.status === "PASSED" && gate.blockerCode)
        context.addIssue({
            code: "custom",
            path: ["blockerCode"],
            message: "Passed readiness gates cannot carry a blocker code.",
        });
    if (gate.status !== "PASSED" && !gate.blockerCode)
        context.addIssue({
            code: "custom",
            path: ["blockerCode"],
            message: "Blocked and external gates require a stable blocker code.",
        });
    if (gate.status === "PASSED" && gate.artifactBindings.length === 0)
        context.addIssue({
            code: "custom",
            path: ["artifactBindings"],
            message: "Passed readiness gates require exact artifact bindings.",
        });
    if (gate.status === "PASSED" && gate.evidenceReferences.length === 0)
        context.addIssue({
            code: "custom",
            path: ["evidenceReferences"],
            message: "Passed readiness gates require typed supporting evidence.",
        });
});
export const bookProductionReadinessV1Schema = z
    .object({
    dimension: bookProductionReadinessDimensionSchema,
    status: z.enum(["NOT_EVALUATED", "BLOCKED", "EXTERNALLY_GATED", "ELIGIBLE"]),
    gateIds: z.array(opaqueIdSchema).max(100),
    blockerCodes: z.array(safeCodeSchema).max(100),
})
    .strict()
    .superRefine((readiness, context) => {
    requireUnique(readiness.gateIds, context, ["gateIds"], "Readiness gate IDs");
    requireUnique(readiness.blockerCodes, context, ["blockerCodes"], "Readiness blocker codes");
});
export const bookProductionTruthBoundaryV1Schema = z
    .object({
    evidenceClass: z.enum(["SIMULATION", "LOCAL_PROOF", "DURABLE_RUNTIME", "DEPLOYED_RUNTIME"]),
    persistence: z.enum(["LOCAL_MANIFEST", "DURABLE_STORE"]),
    externalExecutors: z.enum(["NONE", "SIMULATED", "RECORDED_ACTUAL"]),
    humanDecisions: z.enum(["NONE", "SIMULATED", "RECORDED_ACTUAL"]),
})
    .strict()
    .superRefine((truth, context) => {
    if (truth.evidenceClass === "SIMULATION" && truth.persistence !== "LOCAL_MANIFEST")
        context.addIssue({
            code: "custom",
            path: ["persistence"],
            message: "Simulation evidence cannot claim durable persistence.",
        });
    if (["DURABLE_RUNTIME", "DEPLOYED_RUNTIME"].includes(truth.evidenceClass) &&
        truth.persistence !== "DURABLE_STORE")
        context.addIssue({
            code: "custom",
            path: ["persistence"],
            message: "Runtime evidence requires durable persistence.",
        });
    if (truth.evidenceClass === "SIMULATION" &&
        (truth.externalExecutors === "RECORDED_ACTUAL" || truth.humanDecisions === "RECORDED_ACTUAL"))
        context.addIssue({
            code: "custom",
            path: ["evidenceClass"],
            message: "Simulation cannot claim actual external execution or human decisions.",
        });
});
const productFamily = {
    BEDTIME: "PICTURE_BOOK",
    COMIC: "COMIC_CORE",
    RECIPE: "RECIPE",
    MY_STORY: "NARRATIVE",
    OUR_STORY: "NARRATIVE",
};
const readinessDimensions = bookProductionReadinessDimensionSchema.options;
export const bookProductionEvidenceV1Schema = z
    .object({
    contract: bookProductionEvidenceContractSchema,
    evidenceId: opaqueIdSchema,
    evidenceDigestSha256: sha256Schema,
    product: bookProductionProductSchema,
    productionFamily: bookProductionFamilySchema,
    bookId: opaqueIdSchema,
    revisionId: opaqueIdSchema,
    generatedAt: timestampSchema,
    truth: bookProductionTruthBoundaryV1Schema,
    source: z
        .object({
        sourceSnapshotId: opaqueIdSchema,
        sourceArtifactId: opaqueIdSchema,
        binding: bookGenerationSourceBindingV1Schema,
    })
        .strict(),
    stageGraphBinding: bookProductionVersionBindingV1Schema,
    implementationBinding: bookProductionVersionBindingV1Schema,
    productPolicyBinding: bookProductionVersionBindingV1Schema,
    prompts: z.array(bookProductionPromptEvidenceV1Schema).max(500),
    providerCalls: z.array(bookProductionProviderCallEvidenceV1Schema).max(1_000),
    budget: bookProductionBudgetEvidenceV1Schema,
    artifacts: z.array(bookProductionArtifactV1Schema).min(1).max(2_000),
    assetRefs: z.array(bookProductionAssetReferenceV1Schema).max(5_000),
    stageExecutions: z.array(bookProductionStageExecutionV1Schema).min(1).max(1_000),
    evaluations: z.array(bookProductionEvaluationV1Schema).max(1_000),
    approvals: z.array(bookProductionApprovalV1Schema).max(1_000),
    accessibilityEvidence: z.array(bookProductionAccessibilityEvidenceV1Schema).max(100),
    physicalProofEvidence: z.array(bookProductionPhysicalProofEvidenceV1Schema).max(100),
    deploymentEvidence: z.array(bookProductionDeploymentEvidenceV1Schema).max(100),
    readinessGates: z.array(bookProductionReadinessGateV1Schema).max(500),
    claimedReadiness: z.array(bookProductionReadinessV1Schema).length(readinessDimensions.length),
})
    .strict()
    .superRefine((evidence, context) => {
    if (productFamily[evidence.product] !== evidence.productionFamily)
        context.addIssue({
            code: "custom",
            path: ["productionFamily"],
            message: "The production family must match the named book product.",
        });
    const unique = (values, path, label) => requireUnique(values, context, path, label);
    unique(evidence.prompts.map(({ bindingId }) => bindingId), ["prompts"], "Prompt binding IDs");
    unique(evidence.providerCalls.map(({ callId }) => callId), ["providerCalls"], "Provider call IDs");
    unique(evidence.providerCalls.map(({ provider, providerRequestId }) => `${provider}:${providerRequestId}`), ["providerCalls"], "Provider request identities");
    unique(evidence.budget.reservations.map(({ reservationId }) => reservationId), ["budget", "reservations"], "Budget reservation IDs");
    unique(evidence.budget.reservations.flatMap(({ providerCallId }) => providerCallId ? [providerCallId] : []), ["budget", "reservations"], "Reconciled provider-call reservation bindings");
    unique(evidence.artifacts.map(({ artifactId }) => artifactId), ["artifacts"], "Artifact IDs");
    unique(evidence.assetRefs.map(({ assetId }) => assetId), ["assetRefs"], "Asset IDs");
    unique(evidence.stageExecutions.map(({ executionId }) => executionId), ["stageExecutions"], "Stage execution IDs");
    unique(evidence.stageExecutions.map(({ jobId }) => jobId), ["stageExecutions"], "Stage job IDs");
    unique(evidence.stageExecutions.map(({ rootJobId, stageId, attempt }) => `${rootJobId}:${stageId}:${attempt}`), ["stageExecutions"], "Retry chain coordinates");
    unique(evidence.evaluations.map(({ evaluationId }) => evaluationId), ["evaluations"], "Evaluation IDs");
    unique(evidence.approvals.map(({ approvalId }) => approvalId), ["approvals"], "Approval IDs");
    unique(evidence.accessibilityEvidence.map(({ evidenceId }) => evidenceId), ["accessibilityEvidence"], "Accessibility evidence IDs");
    unique(evidence.physicalProofEvidence.map(({ evidenceId }) => evidenceId), ["physicalProofEvidence"], "Physical-proof evidence IDs");
    unique(evidence.deploymentEvidence.map(({ evidenceId }) => evidenceId), ["deploymentEvidence"], "Deployment evidence IDs");
    unique([
        ...evidence.evaluations.map(({ evaluationId }) => evaluationId),
        ...evidence.approvals.map(({ approvalId }) => approvalId),
        ...evidence.accessibilityEvidence.map(({ evidenceId }) => evidenceId),
        ...evidence.physicalProofEvidence.map(({ evidenceId }) => evidenceId),
        ...evidence.deploymentEvidence.map(({ evidenceId }) => evidenceId),
    ], ["evidenceReferences"], "Evidence IDs across all kinds");
    unique(evidence.readinessGates.map(({ gateId }) => gateId), ["readinessGates"], "Readiness gate IDs");
    unique(evidence.claimedReadiness.map(({ dimension }) => dimension), ["claimedReadiness"], "Claimed readiness dimensions");
    evidence.claimedReadiness.forEach((recorded, index) => {
        if (recorded.dimension !== readinessDimensions[index])
            context.addIssue({
                code: "custom",
                path: ["claimedReadiness", index, "dimension"],
                message: "Claimed readiness must use the canonical common-dimension order.",
            });
    });
});
//# sourceMappingURL=book-production-evidence.js.map